import type { ReactNode } from 'react';
import type { InvokeResponseBase } from '../lib/types';
import { CitationsPanel } from './CitationBadge';

interface AgentResultProps {
  response: InvokeResponseBase;
  /** Composant de rendu du `result` structuré, propre à chaque agent. */
  renderResult?: (result: unknown) => ReactNode;
}

export function AgentResult({ response, renderResult }: AgentResultProps) {
  const status = response.status;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-xs">
        <StatusBadge status={status} />
        <span className="text-taupe">latence : {response.latency_ms} ms</span>
        <span className="text-taupe font-mono">run : {response.agent_run_id}</span>
        {response.confidence !== null && (
          <span className="text-taupe">
            confiance : <span className="font-medium text-ink">{(response.confidence * 100).toFixed(0)}%</span>
          </span>
        )}
      </div>

      {response.prose && (
        <div className="card bg-cream/50">
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">Résumé</div>
          <p className="whitespace-pre-wrap text-ink leading-relaxed">{response.prose}</p>
        </div>
      )}

      {response.awaitingUserInput && (
        <div className="card border-2 border-gold">
          <div className="text-xs uppercase tracking-wider text-gold mb-2">⚠ Confirmation requise</div>
          <p className="text-ink mb-3">{response.awaitingUserInput.question}</p>
          <div className="flex flex-wrap gap-2">
            {response.awaitingUserInput.options.map((opt, i) => (
              <span key={i} className="text-xs px-3 py-1.5 rounded bg-gold/10 border border-gold/40 text-ink">
                {opt.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {response.assumptions && response.assumptions.length > 0 && (
        <div className="card border border-amber-200 bg-amber-50/40">
          <div className="text-xs uppercase tracking-wider text-amber-900 mb-2">
            Hypothèses & avertissements ({response.assumptions.length})
          </div>
          <ul className="list-disc list-inside text-sm text-ink space-y-1">
            {response.assumptions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      {response.result !== null && renderResult && (
        <div className="card">
          <div className="text-xs uppercase tracking-wider text-taupe mb-3">Résultat structuré</div>
          {renderResult(response.result)}
        </div>
      )}

      <div className="card">
        <div className="text-xs uppercase tracking-wider text-taupe mb-3">
          Provenance · {response.citations.length} citation{response.citations.length > 1 ? 's' : ''}
        </div>
        <CitationsPanel citations={response.citations} />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: InvokeResponseBase['status'] }) {
  const map: Record<InvokeResponseBase['status'], { label: string; cls: string }> = {
    completed: { label: 'Terminé', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-300' },
    awaiting_user_input: { label: 'En attente', cls: 'bg-gold/20 text-ink border border-gold' },
    failed: { label: 'Échec', cls: 'bg-red-100 text-red-800 border border-red-300' },
  };
  const { label, cls } = map[status];
  return (
    <span className={`text-xs px-2 py-1 rounded font-medium uppercase tracking-wider ${cls}`}>
      {label}
    </span>
  );
}
